// ABOUTME: Credentials step for the TikTok OAuth finalize modal. Auto-fills description
// ABOUTME: and credentials from the exchanged payload; renders a shop picker when multiple shops were authorized.

import React, { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import Text from '../../../../theme/text/Text';
import { tokens } from '../../../../../theme';
import { setTabErrors, updateData } from '../../../../../newapi/global/progressionTabSlice';
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import { InputDropdown } from '../../../../theme/dropdowns/InputDropdown';
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import { pickTikTokShop, buildTikTokCredentials } from '../../MpAddWebshop/components/tiktokOauth';

const AUTO_DESCRIPTION_PREFIX = 'TikTok — ';
const DEFAULT_DESCRIPTION = 'TikTok Shop';

const Content = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    const [initialized, setInitialized] = useState(false);

    const payload = props.payload;
    const shops = Array.isArray(payload?.shops) ? payload.shops : [];
    const singleShop = pickTikTokShop(shops);

    useEffect(() => {
        if (initialized) return;
        if (!payload) return;

        const description = payload.seller_name
            ? `${AUTO_DESCRIPTION_PREFIX}${payload.seller_name}`
            : DEFAULT_DESCRIPTION;

        dispatch(updateData({
            webshop_type: 'tiktokshop',
            flow_type: 'standard',
            description,
            credentials: singleShop ? buildTikTokCredentials(payload, singleShop) : {},
        }));
        setInitialized(true);
    }, [initialized, dispatch, payload, singleShop]);

    const handleInput = (key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    };

    const handleShopPick = (selectedIndex) => {
        const shop = shops[selectedIndex];
        const credentials = buildTikTokCredentials(payload, shop);
        props.handleDataUpdate({
            credentials: credentials ?? { ...(data.credentials ?? {}) },
        });
    };

    const isMultiShop = shops.length > 1;
    const isConnected = !!data.credentials?.shop_cipher;
    const shopIndex = shops.findIndex(shop => shop.shop_id === data.credentials?.shop_id);

    const { data: relationsData } = useGetRelationsSelectQuery();

    return (
        <div>
            {isFulfilment ? (
                <Box sx={{
                    width: 1, display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 7,
                }}>
                    <Box sx={{ width: 120 }}>
                        <Text>{t('relation')}</Text>
                    </Box>
                    <Box sx={{
                        flex: 1,
                        backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                        borderRadius: 4, display: 'flex',
                    }}>
                        <ShValidatedInputDropdown
                            closeOnSelection
                            searchOptions={{ enabled: true, placeholder: t('search_relation_placeholder') }}
                            name="relationId"
                            height={40}
                            displayName="name"
                            changeField="id"
                            selected={relationsData?.findIndex(r => r.id === data?.relationId) ?? 0}
                            noSelection={t('choose_relation')}
                            options={relationsData ?? []}
                            onChange={handleInput}
                            error={props.errors.relationId}
                        />
                    </Box>
                </Box>
            ) : null}

            <Box sx={{
                width: 1, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 7, mb: 1.5,
                ...(isFulfilment ? { paddingTop: 1.5 } : {}),
            }}>
                <Box sx={{ width: 120 }}>
                    <Text>{t('description')}</Text>
                </Box>
                <Box sx={{
                    flex: 1,
                    backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                    borderRadius: 4, display: 'flex',
                }}>
                    <ShValidatedInput
                        name="description"
                        value={data.description ?? ''}
                        onChange={handleInput}
                        error={props.errors.description}
                    />
                </Box>
            </Box>

            <Box sx={{ width: 1, mt: 2 }}>
                {isMultiShop && (
                    <Box sx={{
                        width: 1, display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', gap: 7, mb: 2,
                    }}>
                        <Box sx={{ width: 120 }}>
                            <Text>{t('tiktok_shop')}</Text>
                        </Box>
                        <Box sx={{
                            flex: 1,
                            backgroundColor: `${theme.palette.mode === 'dark' ? colors.grey[300] : colors.grey[100]}`,
                            borderRadius: 4, display: 'flex',
                        }}>
                            <InputDropdown
                                name="tiktok_shop"
                                noSelection={t('choose_option')}
                                displayName="label"
                                options={shops.map(shop => ({
                                    label: shop.shop_name ? `${shop.shop_name} (${shop.shop_id})` : shop.shop_id,
                                }))}
                                selected={shopIndex}
                                onChange={handleShopPick}
                                closeOnSelection
                                error={props.errors['credentials.shop_cipher']}
                            />
                        </Box>
                    </Box>
                )}

                {isConnected ? (
                    <Box sx={{
                        p: 1.5,
                        backgroundColor: '#e8f5e8',
                        borderRadius: 1,
                        border: '1px solid #4caf50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        <Text variant="body2" color="success.main" sx={{ flex: 1 }}>
                            ✓ {t('tiktok_authentication_successful')}
                            {payload?.seller_name ? ` (${payload.seller_name})` : ''}
                        </Text>
                    </Box>
                ) : (
                    <Box sx={{
                        p: 1.5,
                        backgroundColor: '#ffebee',
                        borderRadius: 1,
                        border: '1px solid #f44336',
                    }}>
                        <Text variant="body2" color="error">
                            {t('tiktok_connection_failed')}
                        </Text>
                    </Box>
                )}
            </Box>
        </div>
    );
};

const Credentials = (props) => {
    const { t } = useTranslation();
    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

    const conditionalSchema = [
        { key: 'description', errorField: t('description'), alwaysRequired: true, type: 'text' },
        {
            key: 'relationId',
            errorField: t('relation'),
            alwaysRequired: isFulfilment,
            condition: () => false,
            type: 'integer',
        },
        {
            key: 'credentials.shop_cipher',
            errorField: t('tiktok_shop'),
            alwaysRequired: true,
            condition: () => false,
            type: 'text',
        },
    ];

    return (
        <ProgressionTab
            tabId="tabCredentials"
            subtitle={t('webshopcredentials')}
            step={1}
            nextTab="tabSync"
            maxSteps={2}
            conditionalSchema={conditionalSchema}
        >
            <Content payload={props.payload} />
        </ProgressionTab>
    );
};

export default Credentials;
